/**
 * GA4 ecommerce event helpers
 * These safely route to the global gtag function.
 */

function gtag(...args: any[]) {
  if (typeof window === 'undefined') return

  if (typeof (window as any).gtag === 'function') {
    // Library is loaded and ready — call directly
    ;(window as any).gtag(...args)
  } else {
    // gtag/js not yet loaded — push to dataLayer queue so the event
    // is replayed once the library initialises (prevents silent drops)
    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).dataLayer.push(args)
  }
}

// --- buildGAItems (shared helper) ----------------------------------------
/** Convert raw cart items array into GA4 items array */
export function buildGAItems(cartItems: any[]): object[] {
  return cartItems.map((item: any) => {
    const product = item.product
    const price =
      item.selectedVariantData?.priceTotal ??
      product?.basePrice ??
      0
    const categories = product?.categories?.filter((c: any) => typeof c === 'object') ?? []
    const variantLabel = item.selectedVariantData?.options
      ?.map((o: any) => `${o.name}: ${o.value}`)
      .join(', ')
    return {
      item_id: String(product?.id ?? ''),
      item_name: product?.title ?? '',
      price,
      quantity: item.quantity ?? 1,
      ...(variantLabel ? { item_variant: variantLabel } : {}),
      ...(categories[0] ? { item_category: categories[0].title } : {}),
    }
  })
}

// --- view_item ------------------------------------------------------------
/** Fire on product detail page mount */
export function trackViewItem(product: any, price: number, currency = 'EUR') {
  const categories = product.categories?.filter((c: any) => typeof c === 'object') ?? []
  gtag('event', 'view_item', {
    currency,
    value: price,
    items: [
      {
        item_id: String(product.id),
        item_name: product.title,
        price,
        quantity: 1,
        ...(categories[0] ? { item_category: categories[0].title } : {}),
      },
    ],
  })
}

// --- add_to_cart ----------------------------------------------------------
/** Fire when an item is successfully added to the cart */
export function trackAddToCart(
  product: any,
  price: number,
  quantity = 1,
  variantLabel?: string,
) {
  const categories = product.categories?.filter((c: any) => typeof c === 'object') ?? []
  gtag('event', 'add_to_cart', {
    currency: 'EUR',
    value: price * quantity,
    items: [
      {
        item_id: String(product.id),
        item_name: product.title,
        price,
        quantity,
        ...(variantLabel ? { item_variant: variantLabel } : {}),
        ...(categories[0] ? { item_category: categories[0].title } : {}),
      },
    ],
  })
}

// --- begin_checkout -------------------------------------------------------
/** Fire when the user lands on the checkout page */
export function trackBeginCheckout(cartItems: any[], totalEur: number) {
  gtag('event', 'begin_checkout', {
    currency: 'EUR',
    value: totalEur,
    items: buildGAItems(cartItems),
  })
}

// --- purchase -------------------------------------------------------------
/** Fire on the order confirmation page after a successful order */
export function trackPurchase({
  orderId,
  totalEur,
  shippingEur = 0,
  cartItems = [],
}: {
  orderId: string
  totalEur: number
  shippingEur?: number
  cartItems?: any[]
  isNewCustomer?: boolean
}) {
  // Standard GA4 ecommerce purchase event
  gtag('event', 'purchase', {
    transaction_id: orderId,
    currency: 'EUR',
    value: totalEur,
    shipping: shippingEur,
    items: buildGAItems(cartItems),
  })

  // NOTE: A Google Ads conversion event (send_to: 'AW-18103701922/...') was
  // removed here — it pointed to a third-party Google Ads account, not yours.
  // If/when you set up your own Google Ads account, add a new conversion event
  // with your own AW-XXXXXXXXXX/label here.
}
